/**
 * Clusters.tsx — Turn 5 · 5b: Consolidate & Decommission Clusters (handoff §2).
 *
 * The drill-in behind Turn 4's "Consolidate" and "Decommission" secondary counts. Shows candidate-
 * merge clusters (survivor + retirees, with the four grouping signals) and a separate decommission-
 * only queue. A VIEW of ClusterRecord / DispositionRecord — every value re-derives from a record.
 * Amber is used only for contested / needs-decision states (turn5 §0).
 */

import { useState } from 'react';
import type { ClusterRecord, NearMissPair } from './types';
import { color, font } from '../tokens';

// Decommission-only queue rows (retiring outright, no survivor). Real data only — no demo set.
type DecommissionItem = { euc_id: string; file: string; dept: string; reason: 'dead' | 'superseded' | 'zero_consumers'; detail: string };

const C = {
  appBg: '#f9fafb', surface: color.surface, ink: '#1e1b3a', body: '#3a3763', label: color.textMuted,
  border: color.cardBorder, accent: color.accent, accentDeep: color.navy, tint: '#a78bfa', amber: '#f59e0b', ink2: '#1e1b3a',
};
const money = (n: number) => `${n} hrs/mo`;

const CONF = (c: ClusterRecord) =>
  c.status === 'contested' || c.confidence < 0.7 ? { label: `CONTESTED · ${c.confidence.toFixed(2)}`, bg: '#fef3c7', fg: '#92400e' }
    : c.confidence >= 0.85 ? { label: `HIGH CONFIDENCE · ${c.confidence.toFixed(2)}`, bg: '#dcfce7', fg: '#166534' }
      : { label: `MEDIUM · ${c.confidence.toFixed(2)}`, bg: '#ede9fe', fg: '#5b21b6' };

const BOX_LABEL: Record<string, string> = { box1: 'BOX 1', box2: 'BOX 2', box3: 'BOX 3', box4: 'BOX 4' };
const RETIRE: Record<string, { label: string; note: string }> = {
  dead: { label: 'DEAD · 0 RUNS 12MO', note: 'Owner attests → archive (G2)' },
  superseded: { label: 'SUPERSEDED', note: 'Reference the system-of-record in attestation' },
  zero_consumers: { label: 'ZERO CONSUMERS', note: 'Notify owner, then retire' },
};

function Kpi({ label, value, tone }: { label: string; value: string | number; tone?: 'amber' }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 16px', flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone === 'amber' ? C.amber : C.ink, letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: 11, color: C.label, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function Signals({ c }: { c: ClusterRecord }) {
  const fp = c.signals.formula_fingerprint_overlap;
  const os = c.signals.output_schema_overlap_columns;
  // Formula overlap + output-schema match come from the file-extraction run, which the flat
  // inventory doesn't carry — show them as "pending" rather than a misleading computed 0.
  const items: [string, string, boolean][] = [
    ['Shared input feeds', c.signals.shared_input_feeds.join(', ') || '—', false],
    ['Formula overlap', fp == null ? 'Pending extraction' : `${Math.round(fp * 100)}%`, fp == null],
    ['Output schema match', os == null ? 'Pending extraction' : `${os} cols`, os == null],
    ['Owner overlap', c.signals.owner_overlap_ratio, false],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginTop: 12 }}>
      {items.map(([k, v, pending], i) => (
        <div key={i} style={{ background: C.appBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 10px' }}>
          <div style={{ fontSize: 11, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Signal {i + 1}</div>
          <div style={{ fontSize: pending ? 10.5 : 11.5, fontWeight: pending ? 600 : 700, fontStyle: pending ? 'italic' : 'normal', color: pending ? C.label : C.ink, marginTop: 2 }}>{v}</div>
          <div style={{ fontSize: 11, color: C.label, marginTop: 1 }}>{k}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Overriding the rule-selected survivor. Spec §5: the default order is overridable, but the reason
 * is required — the whole point of documenting the rule is that a departure from it is defensible.
 */
function SurvivorOverride({ c, onOverride }: { c: ClusterRecord; onOverride: (survivorId: string, reason: string) => Promise<{ ok: boolean; error?: string }> }) {
  const [openForm, setOpenForm] = useState(false);
  const [pick, setPick] = useState(c.survivor_id || c.member_ids[0]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tooShort = reason.trim().length < 10;

  if (!openForm) {
    return (
      <button
        onClick={() => { setOpenForm(true); setError(null); }}
        style={{ marginTop: 10, cursor: 'pointer', background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 10px', fontSize: 11, color: C.body }}
      >
        Override survivor
      </button>
    );
  }
  return (
    <div style={{ marginTop: 10, background: C.appBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: C.label, letterSpacing: '0.05em', marginBottom: 8 }}>OVERRIDE SURVIVOR</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, padding: '6px 8px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink }}
        >
          {c.member_ids.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required) — why this one instead of the rule's pick"
          style={{ flex: 1, minWidth: 220, fontSize: 12, padding: '6px 8px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink }}
        />
        <button
          disabled={tooShort || saving}
          onClick={async () => {
            setSaving(true); setError(null);
            const r = await onOverride(pick, reason.trim());
            setSaving(false);
            if (r.ok) { setOpenForm(false); setReason(''); } else { setError(r.error || 'Could not save the override.'); }
          }}
          style={{ cursor: tooShort || saving ? 'not-allowed' : 'pointer', opacity: tooShort || saving ? 0.5 : 1, background: C.accent, border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, color: '#fff' }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={() => { setOpenForm(false); setError(null); }} style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: 11, color: C.label }}>Cancel</button>
      </div>
      <div style={{ fontSize: 11, color: error ? '#b91c1c' : C.label, marginTop: 6 }}>
        {error || (tooShort ? 'A reason of at least 10 characters is required.' : 'Recorded against the cluster and kept when the inventory is re-ingested.')}
      </div>
    </div>
  );
}

function ClusterCard({ c, expanded, onToggle, onOverride }: { c: ClusterRecord; expanded: boolean; onToggle: () => void; onOverride?: (clusterId: string, survivorId: string, reason: string) => Promise<{ ok: boolean; error?: string }> }) {
  const conf = CONF(c);
  const retirees = c.member_ids.filter((m) => m !== c.survivor_id);
  if (!expanded) {
    return (
      <button onClick={onToggle} style={{ textAlign: 'left', cursor: 'pointer', width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: C.accent }}>{c.id}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{c.member_ids.length} EUCs · survivor: {c.survivor_id || '—'} · retires {retirees.length}</div>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: conf.bg, color: conf.fg }}>{conf.label}</span>
        <span style={{ fontSize: 11, color: C.accent }}>Open →</span>
      </button>
    );
  }
  return (
    <div style={{ background: C.surface, border: `1px solid ${c.status === 'contested' ? 'rgba(245,158,11,.4)' : C.border}`, borderRadius: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: C.accent }}>{c.id}</span>
        <button onClick={onToggle} style={{ marginLeft: 'auto', cursor: 'pointer', background: 'none', border: 'none', fontSize: 11, color: C.label }}>Collapse ▴</button>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: conf.bg, color: conf.fg }}>{conf.label}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Survivor */}
        <div style={{ background: 'linear-gradient(135deg,#efeaff,#f7f4ff)', border: `1px solid ${C.tint}`, borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: C.accentDeep, letterSpacing: '0.05em' }}>SURVIVOR · TAKES THE BOX</span>
            {c.survivor_box && <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 6px', borderRadius: 6, background: C.accent, color: '#fff' }}>{BOX_LABEL[c.survivor_box]}</span>}
          </div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, fontWeight: 700, color: C.ink, marginTop: 6 }}>{c.survivor_id || '— (contested)'}</div>
          <div style={{ fontSize: 11, color: C.body, marginTop: 6 }}><b>Chosen because:</b> {c.survivor_selection_rationale}</div>
          {c.survivor_override && (
            <div style={{ fontSize: 11, color: C.accentDeep, marginTop: 4, fontWeight: 700 }}>Overridden by a consultant</div>
          )}
          {onOverride && <SurvivorOverride c={c} onOverride={(s, r) => onOverride(c.id, s, r)} />}
        </div>
        {/* Retirees */}
        <div style={{ border: `1px dashed ${C.border}`, borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.label, letterSpacing: '0.05em', marginBottom: 6 }}>RETIRES INTO SURVIVOR</div>
          {retirees.length ? retirees.map((m) => (
            <div key={m} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.body, padding: '3px 0' }}>{m}</div>
          )) : <div style={{ fontSize: 11, color: C.label }}>Pending survivor decision</div>}
        </div>
      </div>
      <Signals c={c} />
      {c.dispute && (
        <div style={{ marginTop: 12, background: '#fffaf0', border: `1px solid rgba(245,158,11,.4)`, borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.amber, letterSpacing: '0.05em' }}>NEEDS DECISION</div>
          <div style={{ fontSize: 12, color: C.body, marginTop: 3 }}>{c.dispute.parties.join(' vs. ')} — {c.dispute.question}. Blocks {c.dispute.blocks_count} EUCs.</div>
        </div>
      )}
    </div>
  );
}

const pct = (v: number | null) => (v == null ? 'pending' : `${Math.round(v * 100)}%`);

/**
 * Pairs that scored just under the clustering threshold. Spec §6: they are surfaced as "possible
 * overlap, not confirmed" rather than quietly decided to be unrelated — the algorithm is reporting
 * uncertainty here, and that is a consultant's call to make.
 */
function NearMisses({ pairs }: { pairs: NearMissPair[] }) {
  if (!pairs.length) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Possible overlaps — not confirmed</div>
        <div style={{ fontSize: 11, color: C.label }}>{pairs.length} pair{pairs.length === 1 ? '' : 's'} scored just under the clustering threshold</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pairs.map((p, i) => (
          <div key={`${p.a_id}-${p.b_id}-${i}`} style={{ background: '#fffaf0', border: '1px solid rgba(245,158,11,.35)', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 700, color: C.ink }}>{p.a_id} ↔ {p.b_id}</span>
            <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>SCORE {p.score.toFixed(2)}</span>
            <span style={{ fontSize: 11, color: C.body }}>
              inputs {pct(p.overlaps?.input ?? null)} · formulas {pct(p.overlaps?.formula ?? null)} · output schema {pct(p.overlaps?.output ?? null)}
            </span>
            <span style={{ fontSize: 11, color: C.label, marginLeft: 'auto' }}>Review — the algorithm is not confident either way</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Clusters({ onBack, clusters = [], decommission = [], nearMisses = [], onOverrideSurvivor }: { onBack?: () => void; clusters?: ClusterRecord[]; decommission?: DecommissionItem[]; nearMisses?: NearMissPair[]; onOverrideSurvivor?: (clusterId: string, survivorId: string, reason: string) => Promise<{ ok: boolean; error?: string }> }) {
  const [open, setOpen] = useState<Record<string, boolean>>({ [clusters[0]?.id]: true, [clusters[1]?.id]: true });
  const contested = clusters.filter((c) => c.status === 'contested').length;
  const survivors = clusters.filter((c) => c.survivor_id).length;
  const hrs = clusters.reduce((t, c) => t + c.estimated_monthly_hours_saved, 0);

  return (
    <div style={{ fontFamily: font.ui }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: '-0.02em' }}>Consolidate & Decommission</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.label }}>Candidate-merge clusters (survivor + retirees) and the decommission-only queue. Retirement is reversible for 90 days.</p>
        </div>
        {onBack && <button onClick={onBack} style={{ cursor: 'pointer', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px', fontSize: 12.5, color: C.body }}>← Back to framework</button>}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <Kpi label="Clusters" value={clusters.length} />
        <Kpi label="To consolidate (survivors)" value={survivors} />
        <Kpi label="To decommission" value={decommission.length} />
        <Kpi label="Contested" value={contested} tone={contested ? 'amber' : undefined} />
        <Kpi label="Possible overlaps" value={nearMisses.length} tone={nearMisses.length ? 'amber' : undefined} />
        <Kpi label="Est. hrs saved / mo" value={money(hrs)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {clusters.map((c) => (
          <ClusterCard key={c.id} c={c} expanded={!!open[c.id]} onToggle={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))} onOverride={onOverrideSurvivor} />
        ))}
      </div>

      <NearMisses pairs={nearMisses} />

      {/* Decommission-only queue */}
      <div style={{ background: C.ink2, borderRadius: 18, padding: 18, color: '#e7e6f4' }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 2 }}>Decommission queue</div>
        <div style={{ fontSize: 11, color: '#9a97c4', marginBottom: 14 }}>Retiring outright — no survivor. Retirement requires owner + TSG attestation; reversible for 90 days.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {(['dead', 'superseded', 'zero_consumers'] as const).map((reason) => {
            const rows = decommission.filter((d) => d.reason === reason);
            return (
              <div key={reason} style={{ background: 'rgba(255,255,255,.05)', borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#c9c6ee', letterSpacing: '0.04em' }}>{RETIRE[reason].label} · {rows.length}</div>
                {rows.map((d) => (
                  <div key={d.euc_id} style={{ marginTop: 8 }}>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#e7e3ff' }}>{d.file}</div>
                    <div style={{ fontSize: 11, color: '#8b8bad' }}>{d.detail}</div>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: '#7d7ab0', marginTop: 10, borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 6 }}>{RETIRE[reason].note}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
